// Recepción — open a new session (/po/scan/nuevo). Two ways in: scan/type a
// PO-label barcode ("O" + store(2) + onum, the bc-rcv.p convention), or pick
// from the list of open POs (reusing the existing purchasing fetcher).
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Button, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, TextInput, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchPurchases } from '../api/purchasing';
import { openReception } from '../api/reception';
import AccessDenied from '../components/AccessDenied';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import TableSkeleton from '../components/TableSkeleton';
import { canUseReceiving } from '../lib/roles';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { Purchase, User } from '../api/types';

/**
 * Parse a scanned/typed PO-label barcode into {sid, onum}. Format (bc-rcv.p,
 * the ERP RF-scanner precedent, `pacesett404b/bc-rcv.p`): literal "O" + a
 * 2-digit store id + 1 reserved character + the onum (rest of the string).
 * Returns null when the code doesn't match — the caller shows a "código no
 * válido" error instead of guessing.
 */
export function parsePoLabel(raw: string): { sid: number; onum: number } | null {
  const code = raw.trim().toUpperCase();
  if (code.length < 6 || code[0] !== 'O') return null;
  const sidPart = code.slice(1, 3);
  const onumPart = code.slice(4);
  if (!/^\d{2}$/.test(sidPart) || !/^\d+$/.test(onumPart)) return null;
  const sid = Number(sidPart);
  const onum = Number(onumPart);
  if (!Number.isFinite(sid) || !Number.isFinite(onum) || onum <= 0) return null;
  return { sid, onum };
}

/** today minus N days as YYYY-MM-DD — a wide-enough window that any PO still
 *  open today is virtually certain to have been created within it. */
function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

export default function ReceptionNew() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {canUseReceiving(user) ? <ReceptionNewInner user={user} /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}

function ReceptionNewInner({ user }: { user: User }) {
  const navigate = useNavigate();

  const [scanCode, setScanCode] = useState('');
  const [opening, setOpening] = useState(false);
  const [openErr, setOpenErr] = useState<string | null>(null);

  const [purchases, setPurchases] = useState<Purchase[] | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const loadOpenPurchases = () => {
    setListErr(null);
    setPurchases(null);
    fetchPurchases({
      from: daysAgo(365),
      to: localYMD(),
      sid: user.sid_assigned,
      status: 'open',
      limit: 200,
    })
      .then(setPurchases)
      .catch((e: Error) => setListErr(e.message ?? String(e)));
  };
  useEffect(() => { loadOpenPurchases(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  async function open(sid: number, onum: number) {
    setOpenErr(null);
    setOpening(true);
    try {
      const session = await openReception(sid, onum);
      navigate(`/po/scan/${session.id}`);
    } catch (e) {
      setOpenErr((e as Error).message ?? t.recepcion.errors.generic);
    } finally {
      setOpening(false);
    }
  }

  function handleScanSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = parsePoLabel(scanCode);
    if (!parsed) {
      setOpenErr(t.recepcion.new.scanInvalid);
      return;
    }
    void open(parsed.sid, parsed.onum);
  }

  const filteredPurchases = (purchases ?? []).filter((p) => {
    if (!search.trim()) return true;
    const q = search.trim().toUpperCase();
    return (
      String(p.onum).includes(q) ||
      p.sup_id.toUpperCase().includes(q) ||
      p.sup_name.toUpperCase().includes(q)
    );
  });

  return (
    <div className="space-y-4 max-w-3xl">
      <Title>{t.recepcion.new.title}</Title>

      <Card className="space-y-3">
        <Title className="text-base">{t.recepcion.new.scanTab}</Title>
        <Text className="text-xs text-tremor-content">{t.recepcion.new.scanHint}</Text>
        <form onSubmit={handleScanSubmit} className="flex flex-wrap items-end gap-3">
          <label className="text-sm flex-1 min-w-[220px]">
            <div className="text-tremor-content text-xs mb-1">{t.recepcion.new.scanLabel}</div>
            <TextInput
              value={scanCode}
              onValueChange={setScanCode}
              placeholder={t.recepcion.new.scanPlaceholder}
              autoFocus
            />
          </label>
          <Button type="submit" disabled={opening || scanCode.trim() === ''} loading={opening}>
            {t.recepcion.new.submit}
          </Button>
        </form>
      </Card>

      {openErr && <Text className="text-red-600 dark:text-red-400 text-sm">{openErr}</Text>}

      <Card className="space-y-3">
        <Title className="text-base">{t.recepcion.new.pickTab}</Title>
        <TextInput
          value={search}
          onValueChange={setSearch}
          placeholder={t.recepcion.new.searchPlaceholder}
        />
        {listErr && <ErrorRetry message={listErr} onRetry={loadOpenPurchases} />}
        {!listErr && purchases === null && <TableSkeleton rows={5} cols={5} bare />}
        {!listErr && purchases !== null && filteredPurchases.length === 0 && (
          <EmptyState message={t.recepcion.new.empty} bare />
        )}
        {!listErr && filteredPurchases.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.recepcion.new.cols.onum}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.new.cols.date}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.new.cols.supplier}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.new.cols.store}</TableHeaderCell>
                  <TableHeaderCell>{t.recepcion.new.cols.lines}</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPurchases.map((p) => (
                  <TableRow key={`${p.sid}-${p.onum}`}>
                    <TableCell className="font-medium">#{p.onum}</TableCell>
                    <TableCell>{p.cdate}</TableCell>
                    <TableCell>{p.sup_name || p.sup_id}</TableCell>
                    <TableCell>{p.sid}</TableCell>
                    <TableCell>{p.lines}</TableCell>
                    <TableCell>
                      <Button size="xs" disabled={opening} onClick={() => void open(p.sid, p.onum)}>
                        {t.recepcion.new.open}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
